import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Search, Filter, Eye, Edit2, Trash2, Plus, Users, AlertCircle } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const ROLE_OPTIONS = [
  { value: 'all', label: 'All Roles' },
  { value: 'super_admin', label: 'Super Admin' },
  { value: 'company_admin', label: 'Company Admin' },
  { value: 'manager', label: 'Manager' },
  { value: 'driver', label: 'Driver' },
  { value: 'accountant', label: 'Accountant' },
  { value: 'warehouse_staff', label: 'Warehouse Staff' }
];

const UserManagement = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewUser, setViewUser] = useState(null);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    if (!token) {
      throw new Error('Please log in to manage users.');
    }

    return {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  const getErrorMessage = async (response, fallbackMessage) => {
    try {
      const data = await response.json();
      return data?.detail || fallbackMessage;
    } catch (error) {
      return fallbackMessage;
    }
  };

  const fetchUsers = async () => {
    try {
      setLoading(true);
      setErrorMessage('');
      setSuccessMessage('');

      if (!backendUrl) {
        throw new Error('Backend URL is not configured.');
      }

      const headers = getAuthHeaders();
      const response = await fetch(`${backendUrl}/api/users`, { headers });
      if (!response.ok) {
        const message = await getErrorMessage(response, 'Failed to load users.');
        throw new Error(message);
      }

      const data = await response.json();
      setUsers(data || []);
    } catch (error) {
      setUsers([]);
      setErrorMessage(error.message || 'Failed to load users.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      setErrorMessage('');
      setSuccessMessage('');

      const headers = getAuthHeaders();
      const response = await fetch(`${backendUrl}/api/users/${userToDelete.id}`, {
        method: 'DELETE',
        headers
      });

      if (!response.ok) {
        const message = await getErrorMessage(response, 'Failed to delete user.');
        throw new Error(message);
      }

      setDeleteDialogOpen(false);
      setUserToDelete(null);
      setSuccessMessage('User deleted successfully.');
      await fetchUsers();
    } catch (error) {
      setErrorMessage(error.message || 'Failed to delete user.');
    }
  };

  const handleViewClick = (user) => {
    setViewUser(user);
    setViewDialogOpen(true);
  };

  const filteredUsers = users.filter(user => {
    const fullName = user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim();
    const matchesSearch =
      fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (user.email || '').toLowerCase().includes(searchQuery.toLowerCase()) ||
      (user.username || '').toLowerCase().includes(searchQuery.toLowerCase());

    const matchesRole = roleFilter === 'all' || user.role === roleFilter;

    return matchesSearch && matchesRole;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users className="w-6 h-6 text-accent-blue" />
          <div>
            <h2 className="text-2xl font-bold text-text-dark font-manrope">Users</h2>
            <p className="text-sm text-text-secondary font-ibm">Manage employees and permissions</p>
          </div>
        </div>
        <Button
          data-testid="add-user-button"
          onClick={() => navigate('/settings/users/add')}
          className="bg-accent-green hover:bg-accent-green/90 text-white font-ibm font-semibold h-11 px-6 rounded-lg"
        >
          <Plus className="w-5 h-5 mr-2" />
          Add User
        </Button>
      </div>

      {(errorMessage || successMessage) && (
        <div className="space-y-3">
          {errorMessage && (
            <div
              data-testid="user-management-error"
              className="rounded-lg border border-accent-red/40 bg-accent-red/10 px-4 py-3 text-sm text-accent-red font-ibm"
            >
              {errorMessage}
            </div>
          )}
          {successMessage && (
            <div
              data-testid="user-management-success"
              className="rounded-lg border border-accent-green/40 bg-accent-green/10 px-4 py-3 text-sm text-accent-green font-ibm"
            >
              {successMessage}
            </div>
          )}
        </div>
      )}

      <Card className="bg-primary-blue text-white rounded-xl shadow-lg">
        <div className="p-6">
          <div className="flex items-center justify-between">
            <h3 data-testid="users-count" className="text-xl font-semibold font-manrope">
              All Users ({filteredUsers.length})
            </h3>

            <div className="flex items-center gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-white/60" />
                <Input
                  data-testid="user-search-input"
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-64 bg-white/10 border-white/20 text-white placeholder:text-white/60 focus:bg-white/20 focus:border-white/40 font-ibm"
                />
              </div>

              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger data-testid="role-filter-select" className="w-44 bg-white/10 border-white/20 text-white focus:bg-white/20 focus:border-white/40 font-ibm">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map(option => (
                    <SelectItem key={option.value} data-testid={`role-filter-${option.value}`} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </Card>

      <Card className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
        {loading ? (
          <div data-testid="users-loading" className="p-12 text-center text-text-secondary font-ibm">
            Loading users...
          </div>
        ) : filteredUsers.length === 0 ? (
          <div data-testid="users-empty" className="p-12 text-center text-text-secondary font-ibm">
            No users found
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50/50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm">Name</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm">Email</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm">Role</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm">Company</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm">Branch</th>
                  <th className="px-6 py-4 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm">Phone</th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm">Status</th>
                  <th className="px-6 py-4 text-center text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredUsers.map(user => (
                  <tr key={user.id} data-testid={`user-row-${user.id}`} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <span data-testid={`user-name-${user.id}`} className="text-sm font-semibold text-text-dark font-ibm">
                        {user.full_name || `${user.first_name || ''} ${user.last_name || ''}`.trim()}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span data-testid={`user-email-${user.id}`} className="text-sm text-text-secondary font-ibm">
                        {user.email}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span data-testid={`user-role-${user.id}`} className="text-sm text-text-secondary font-ibm">
                        {user.role}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span data-testid={`user-company-${user.id}`} className="text-sm text-text-secondary font-ibm">
                        {user.company_name || '—'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span data-testid={`user-branch-${user.id}`} className="text-sm text-text-secondary font-ibm">
                        {user.branch_name || '—'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span data-testid={`user-phone-${user.id}`} className="text-sm text-text-secondary font-ibm">
                        {user.phone || '—'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <Badge
                        data-testid={`user-status-${user.id}`}
                        className={`font-ibm font-semibold border-0 ${user.status === 'active' ? 'bg-accent-green/15 text-accent-green' : 'bg-gray-200 text-gray-600'}`}
                      >
                        {user.status}
                      </Badge>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          data-testid={`view-user-${user.id}`}
                          onClick={() => handleViewClick(user)}
                          className="w-8 h-8 flex items-center justify-center hover:bg-accent-blue/10 rounded-lg transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4 text-accent-blue hover:text-accent-blue/80" />
                        </button>
                        <button
                          data-testid={`edit-user-${user.id}`}
                          onClick={() => navigate(`/settings/users/edit/${user.id}`)}
                          className="w-8 h-8 flex items-center justify-center hover:bg-accent-orange/10 rounded-lg transition-colors"
                          title="Edit User"
                        >
                          <Edit2 className="w-4 h-4 text-accent-orange hover:text-accent-orange/80" />
                        </button>
                        <button
                          data-testid={`delete-user-${user.id}`}
                          onClick={() => handleDeleteClick(user)}
                          className="w-8 h-8 flex items-center justify-center hover:bg-accent-red/10 rounded-lg transition-colors"
                          title="Delete User"
                        >
                          <Trash2 className="w-4 h-4 text-accent-red hover:text-accent-red/80" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="delete-user-title" className="flex items-center gap-2 text-text-dark font-manrope">
              <AlertCircle className="w-5 h-5 text-accent-red" />
              Delete User
            </AlertDialogTitle>
            <AlertDialogDescription data-testid="delete-user-description" className="text-text-secondary font-ibm">
              Are you sure you want to delete <span className="font-semibold text-text-dark">{userToDelete?.full_name}</span>?
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-user-cancel" className="font-ibm">Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-user-confirm"
              onClick={handleDeleteConfirm}
              className="bg-accent-red hover:bg-accent-red/90 font-ibm"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <AlertDialogContent className="bg-white max-w-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle data-testid="user-details-title" className="flex items-center gap-2 text-text-dark font-manrope text-2xl">
              <Users className="w-6 h-6 text-accent-blue" />
              User Details
            </AlertDialogTitle>
          </AlertDialogHeader>

          {viewUser && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm mb-1">Name</p>
                  <p data-testid="user-details-name" className="text-base font-semibold text-text-dark font-ibm">
                    {viewUser.full_name || `${viewUser.first_name || ''} ${viewUser.last_name || ''}`.trim()}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm mb-1">Email</p>
                  <p data-testid="user-details-email" className="text-base text-text-dark font-ibm">{viewUser.email}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm mb-1">Role</p>
                  <p data-testid="user-details-role" className="text-base text-text-dark font-ibm">{viewUser.role}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm mb-1">Company</p>
                  <p data-testid="user-details-company" className="text-base text-text-dark font-ibm">{viewUser.company_name || '—'}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm mb-1">Branch</p>
                  <p data-testid="user-details-branch" className="text-base text-text-dark font-ibm">{viewUser.branch_name || '—'}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm mb-1">Phone</p>
                  <p data-testid="user-details-phone" className="text-base text-text-dark font-ibm">{viewUser.phone || '—'}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm mb-1">Status</p>
                  <p data-testid="user-details-status" className="text-base text-text-dark font-ibm">{viewUser.status}</p>
                </div>
              </div>
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel data-testid="user-details-close" className="font-ibm">Close</AlertDialogCancel>
            <Button
              data-testid="user-details-edit"
              onClick={() => {
                setViewDialogOpen(false);
                navigate(`/settings/users/edit/${viewUser.id}`);
              }}
              className="bg-accent-blue hover:bg-accent-blue/90 text-white font-ibm"
            >
              <Edit2 className="w-4 h-4 mr-2" />
              Edit User
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UserManagement;
